import { APIProvider, Map } from "@vis.gl/react-google-maps";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import FloatingSearch from "../../mission-control/FloatingSearch";
import useLiveLocation from "../../../hooks/useLiveLocation";
import MarkerClusterGM from "../google/MarkerClusterGM";
import Polyline from "../google/Polyline";
import {
  locationSocketUrl,
  mapApiKey,
  pathColors,
  polylinePathType,
  STATUS_GROUPS,
  statusConst,
  SYSTEM_CONFIGS,
} from "../../../const";
import MoreInfo from "../../mission-control/MoreInfo/MoreInfo";
import "../../../css/MissionControll.css";
import LocationMarker from "../google/LocationMarker";
import { missionControlActions } from "../../../store/mission-control/mission-control-slice";
import SideNav from "../../mission-control/SideNav";
import { Settings, Sliders } from "react-feather";
import SearchFiltration from "../../mission-control/SearchFilter/SearchFiltration";
import MapSettings from "../../mission-control/MapSettings";
import { getDropdownData } from "../../../api/keyvalue";
import LoadingSpinner from "../../LoadingSpinner";
import WebSocketConnection from "../../../service/WebSocketConnection";
import { getSystemConfig } from "../../../api/systemConfigApi";
import { statusByContext } from "../../../api/statusApi";

const API_KEY = mapApiKey;

const MissionControlMapGM = () => {
  const results = useSelector((state) => state.missionControl.results);
  const showPath = useSelector((state) => state.missionControl.showPath);
  const [vehicleLiveLocations, setVehicleLiveLocations] = useState(null);
  const [vehiclePathData, setVehiclePathData] = useState({});
  const [showVehicleJobInfo, setShowVehicleJobInfo] = useState(false);
  const [advancedDirectionResponse, setAdvancedDirectionResponse] =
    useState(null);
  const [advancedDirectionVehicle, setAdvancedDirectionVehicle] =
    useState(null);

  const [openSideNav, setOpenSideNav] = useState(false);

  const [eventOptions, setEventOptions] = useState([]);
  const [jobStatusOptions, setJobStatusOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const apiCount = useSelector((state) => state.missionControl.apiCallCount);
  const vehicleCount = useSelector(
    (state) => state.missionControl.mapSettings.markerLimit,
  );
  const dispatch = useDispatch();

  useEffect(() => {
    (async () => {
      const vehicleLimit = await getSystemConfig(
        SYSTEM_CONFIGS.MISSION_CONTROL_VEHICLE_LIMIT,
        "100",
      );
      dispatch(
        missionControlActions.addOrUpdateMapSettings({
          key: "markerLimit",
          value: parseInt(vehicleLimit),
        }),
      );
    })();
  }, [dispatch]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const locationEvents = await getDropdownData("location_event");
      const jobStatus = await statusByContext(STATUS_GROUPS.MISSION_CONTROL);
      setJobStatusOptions(jobStatus);
      setEventOptions(locationEvents);
      setIsLoading(false);
    })();
  }, []);

  console.log("Total count - " + apiCount);
  console.log("Vehicle Count - " + vehicleCount);

  const [job, setJob] = useState(null);
  const [vehNo, setVehNo] = useState("N/A");
  const [vehModel, setVehModel] = useState("N/A");
  const [vehManufacturer, setVehManufacturer] = useState("N/A");

  const showAdvancedDirection = useSelector(
    (state) =>
      state.missionControl.advancedDirections[advancedDirectionVehicle],
  );

  const jobLocation = useSelector(
    (state) => state.missionControl.jobLocation[advancedDirectionVehicle],
  );

  const handleAdvancedDirectionResponse = useCallback(
    (part1, part2, terminalId) => {
      setAdvancedDirectionResponse([part1, part2]);
      setAdvancedDirectionVehicle(terminalId);
    },
    [],
  );

  const closeJobInfo = () => {
    setShowVehicleJobInfo(false);
  };

  const jobInfoShowHandler = (jobId, vehicleNo, model, manufacturer) => {
    setJob(jobId);
    setVehNo(vehicleNo);
    setVehModel(model);
    setVehManufacturer(manufacturer);

    setShowVehicleJobInfo((prevState) => !prevState);
  };

  const user = useSelector((state) => state.user);

  const wsConnection = useMemo(
    () => new WebSocketConnection(locationSocketUrl, {}),
    [],
  );

  const onMessage = useCallback((data) => {
    let id = 1;
    if (Object.entries(data) <= 0) {
      setVehicleLiveLocations({});
      setVehiclePathData({});
    }
    for (let [terminalId, locationData] of Object.entries(data)) {
      const pathColor = pathColors[id++];
      setVehicleLiveLocations((prevState) => ({
        ...prevState,
        [terminalId]: locationData,
      }));

      setVehiclePathData((prevState) => ({
        ...prevState,
        [terminalId]: prevState[terminalId]
          ? {
              ...prevState[terminalId],
              path: prevState[terminalId].path
                ? [
                    ...prevState[terminalId].path,
                    {
                      lat: locationData.latitude,
                      lng: locationData.longitude,
                    },
                  ]
                : [
                    {
                      lat: locationData.latitude,
                      lng: locationData.longitude,
                    },
                  ],
            }
          : {
              color: pathColor,
              path: [
                { lat: locationData.latitude, lng: locationData.longitude },
              ],
            },
      }));
    }
  }, []);

  const payload = useMemo(
    () => ({
      username: user.username,
      terminalIds: results,
    }),
    [results, user.username],
  );

  const headers = useMemo(() => ({ "content-type": "application/json" }), []);

  const beforeSend = useCallback(() => setVehicleLiveLocations({}), []);

  const { wsStatus, loadingMessage } = useLiveLocation({
    wsConnection,
    messageMapping: {
      endpoint: `/locationservice/mission-control/live-location/${user.username}`,
      headers,
      payload,
    },
    topic: `/topic/mission-control/liveLocation/${user.username}`,
    onMessage,
    beforeSend,
  });

  let unvisitedIndex = 0;

  return (
    <>
      {wsStatus !== statusConst.SUCCESS ? (
        <LoadingSpinner
          message={loadingMessage}
          spinner={wsStatus === statusConst.FAILURE ? "error" : "gps-loader"}
        />
      ) : (
        <div className="mission-control-map-container" style={{ width: "98%" }}>
          <SideNav
            sideNavHandler={(value) => {
              setOpenSideNav(value);
            }}
            isSideNavOpen={openSideNav}
            items={{
              "search-filtration": {
                Icon: Sliders,
                content: (
                  <SearchFiltration
                    eventOptions={eventOptions}
                    jobStatusOptions={jobStatusOptions}
                    isLoading={isLoading}
                  />
                ),
              },
              "map-settings": {
                Icon: Settings,
                content: <MapSettings isLoading={false} />,
              },
            }}
          />
          <FloatingSearch
            style={{
              left:
                showVehicleJobInfo && openSideNav
                  ? "500px"
                  : showVehicleJobInfo
                    ? "700px"
                    : openSideNav
                      ? "500px"
                      : "80px",
            }}
          />
          {showVehicleJobInfo && (
            <MoreInfo
              style={{
                top: openSideNav ? "70px" : "20px",
                left: openSideNav ? "500px" : "80px",
              }}
              isSideNavOpen={openSideNav}
              job={job}
              vehNo={vehNo}
              model={vehModel}
              manufacturer={vehManufacturer}
              closeJobInfo={closeJobInfo}
            />
          )}
          <APIProvider apiKey={API_KEY}>
            <Map
              reuseMaps={true}
              style={{ width: "100vw", height: "100vh" }}
              defaultCenter={{ lat: 6.9271, lng: 79.861244 }}
              defaultZoom={8}
              gestureHandling={"greedy"}
              disableDefaultUI={true}
              mapId={"mission-control-map-gm"}
            >
              {vehicleLiveLocations &&
                Object.values(vehicleLiveLocations).length > 0 && (
                  <MarkerClusterGM
                    vehicles={Object.values(vehicleLiveLocations)}
                    jobVehicleInfoToggle={jobInfoShowHandler}
                    handleAdvancedDirectionResponse={
                      handleAdvancedDirectionResponse
                    }
                    advancedDirectionVehicle={advancedDirectionVehicle}
                  />
                )}
              {Object.entries(showPath).map(([terminalId, show]) => {
                if (show) {
                  return (
                    <Polyline
                      key={terminalId}
                      strokeWeight={5}
                      strokeColor={vehiclePathData[terminalId].color}
                      path={vehiclePathData[terminalId].path}
                    />
                  );
                } else {
                  return <></>;
                }
              })}
              {showAdvancedDirection && advancedDirectionResponse && (
                <>
                  <Polyline
                    strokeWeight={5}
                    strokeColor={"blue"}
                    strokeOpacity={1}
                    pathType={polylinePathType.ENCODED_PATH_SEGMENT}
                    encodedPathSegments={advancedDirectionResponse[0].routes[0].legs.reduce(
                      (acc, leg) => {
                        leg.steps.forEach((step) => {
                          acc.push(step.encoded_lat_lngs);
                        });
                        return acc;
                      },
                      [],
                    )}
                  />
                  <Polyline
                    strokeWeight={5}
                    strokeColor={"red"}
                    strokeOpacity={0.5}
                    pathType={polylinePathType.ENCODED_PATH_SEGMENT}
                    encodedPathSegments={advancedDirectionResponse[1].routes[0].legs.reduce(
                      (acc, leg) => {
                        leg.steps.forEach((step) => {
                          acc.push(step.encoded_lat_lngs);
                        });
                        return acc;
                      },
                      [],
                    )}
                  />
                  {jobLocation &&
                    jobLocation.locations &&
                    jobLocation.locations.length > 0 &&
                    jobLocation.locations.map((loc) => {
                      const unvisitedDirectionResponse =
                        advancedDirectionResponse[1];

                      const unvisitedRequest = [
                        unvisitedDirectionResponse.request.origin.location,
                        unvisitedDirectionResponse.request.destination.location,
                        ...unvisitedDirectionResponse.request.waypoints.map(
                          (waypoint) => waypoint.location.location,
                        ),
                      ];

                      let unvisited = false;

                      const currentLocation = new window.google.maps.LatLng({
                        lat: loc.locaLat,
                        lng: loc.locaLon,
                      });
                      if (
                        unvisitedRequest.some((element) =>
                          element.equals(currentLocation),
                        )
                      ) {
                        unvisited = true;
                        ++unvisitedIndex;
                        dispatch(
                          missionControlActions.setUnvisitedDirections(
                            unvisitedDirectionResponse.routes[0].legs,
                          ),
                        );
                      }
                      return (
                        <LocationMarker
                          key={loc.rowId}
                          position={currentLocation}
                          type={
                            (loc.locTypId === 1 || loc.locTypId === 2) &&
                            jobLocation.sameStartAndEnd
                              ? 4
                              : loc.locTypId
                          }
                          jobId={loc.jobId}
                          locId={loc.locaId}
                          name={loc.locaName}
                          typeName={
                            (loc.locTypId === 1 || loc.locTypId === 2) &&
                            jobLocation.sameStartAndEnd
                              ? "Start and End Location"
                              : loc.locType
                          }
                          unvisited={unvisited}
                          unvisitedIndex={unvisitedIndex}
                          maxSeq={jobLocation.length}
                        />
                      );
                    })}
                </>
              )}
            </Map>
          </APIProvider>
        </div>
      )}
    </>
  );
};

export default MissionControlMapGM;
